import React from 'react';
import { setupIonicReact } from '@ionic/react';
import { IonApp, IonRouterOutlet } from '@ionic/react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider } from './contexts/AuthContext';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

/* Global styles */
import './App.css';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Wallet from './pages/Wallet';
import Games from './pages/Games';
import SpinWheel from './pages/SpinWheel';
import ScratchCard from './pages/ScratchCard';
import MemoryMatch from './pages/MemoryMatch';
import Game2048 from './pages/Game2048';
import SnakeGame from './pages/SnakeGame';
import BubbleShooter from './pages/BubbleShooter';
import WordSearch from './pages/WordSearch';
import WatchAd from './pages/WatchAd';
import Profile from './pages/Profile';
import About from './pages/About';
import PaymentInfo from './pages/PaymentInfo';
import GiftCardRedemption from './pages/GiftCardRedemption';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { WalletProvider } from './contexts/WalletContext';
import { LevelProvider } from './contexts/LevelContext';
import { AppLoadingProvider } from './components/AppLoadingProvider';

setupIonicReact();

const App: React.FC = () => {
  // Error boundary for catching React errors
  React.useEffect(() => {
    window.addEventListener('error', (event) => {
      // Some errors (like resource loading) may have null error object
      if (event.error) {
        console.error('Global error:', event.error);
      } else if (event.message) {
        console.error('Global error (no error object):', event.message, event.filename, event.lineno);
      }
      // Silently ignore null errors - they're often harmless resource loading issues
    });
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    });
  }, []);

  return (
    <ErrorBoundary>
      <IonApp>
        <AuthProvider>
          <WalletProvider>
            <LevelProvider>
              <AppLoadingProvider>
            {Capacitor.isNativePlatform() ? (
              <HashRouter>
                <IonRouterOutlet>
                  <Routes>
                    <Route path="/home" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/wallet"
                      element={
                        <ProtectedRoute>
                          <Wallet />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/games"
                      element={
                        <ProtectedRoute>
                          <Games />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/spin-wheel"
                      element={
                        <ProtectedRoute>
                          <SpinWheel />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/scratch-card"
                      element={
                        <ProtectedRoute>
                          <ScratchCard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/memory-match"
                      element={
                        <ProtectedRoute>
                          <MemoryMatch />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/game-2048"
                      element={
                        <ProtectedRoute>
                          <Game2048 />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/snake-game"
                      element={
                        <ProtectedRoute>
                          <SnakeGame />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bubble-shooter"
                      element={
                        <ProtectedRoute>
                          <BubbleShooter />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/word-search"
                      element={
                        <ProtectedRoute>
                          <WordSearch />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/watch-ad"
                      element={
                        <ProtectedRoute>
                          <WatchAd />
                        </ProtectedRoute>
                      }
                    />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/about"
                    element={
                      <ProtectedRoute>
                        <About />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payment-info"
                    element={
                      <ProtectedRoute>
                        <PaymentInfo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gift-card-redemption"
                    element={
                      <ProtectedRoute>
                        <GiftCardRedemption />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<Navigate to="/login" replace />} />
                </Routes>
              </IonRouterOutlet>
            </HashRouter>
          ) : (
            <BrowserRouter>
              <IonRouterOutlet>
                <Routes>
                  <Route path="/home" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wallet"
                    element={
                      <ProtectedRoute>
                        <Wallet />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/games"
                    element={
                      <ProtectedRoute>
                        <Games />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/spin-wheel"
                    element={
                      <ProtectedRoute>
                        <SpinWheel />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scratch-card"
                    element={
                      <ProtectedRoute>
                        <ScratchCard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/memory-match"
                    element={
                      <ProtectedRoute>
                        <MemoryMatch />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/game-2048"
                    element={
                      <ProtectedRoute>
                        <Game2048 />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/snake-game"
                    element={
                      <ProtectedRoute>
                        <SnakeGame />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bubble-shooter"
                    element={
                      <ProtectedRoute>
                        <BubbleShooter />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/word-search"
                    element={
                      <ProtectedRoute>
                        <WordSearch />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/watch-ad"
                    element={
                      <ProtectedRoute>
                        <WatchAd />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/about"
                    element={
                      <ProtectedRoute>
                        <About />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payment-info"
                    element={
                      <ProtectedRoute>
                        <PaymentInfo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gift-card-redemption"
                    element={
                      <ProtectedRoute>
                        <GiftCardRedemption />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  </Routes>
                </IonRouterOutlet>
              </BrowserRouter>
            )}
              </AppLoadingProvider>
            </LevelProvider>
          </WalletProvider>
        </AuthProvider>
      </IonApp>
    </ErrorBoundary>
  );
};

export default App;
